import { dayjs, formatDateRange } from "@/core/utils/date";
import { formatPhoneNumber } from "@/core/utils/phone";
import { isSmsDeliveryWithCarrier } from "@/payroll/pay-breakdown/payroll-statement-notifications/delivery-status-rules";
import type {
  PayrollStatementNotificationDeliveryStatus,
  PayrollStatementSentPack,
  PayrollStatementSmsDelivery,
  PayrollStatementSmsDeliveryStatus,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/types";

const REASON_LABELS: Record<string, string> = {
  pay_period_not_found: "Pay period not found",
  completed_statement_run_not_found: "No completed PayPack run",
  missing_email: "Missing saved email",
  missing_phone: "No phone number to text",
  sms_opted_out: "Opted out of texts",
  owner_preview_only: "Managers preview only",
  statement_not_available: "PayPack preview unavailable",
};

const STATUS_LABELS: Record<
  PayrollStatementNotificationDeliveryStatus,
  string
> = {
  pending: "Pending",
  enqueued: "Queued",
  succeeded: "Sent",
  failed: "Failed",
  skipped: "Skipped",
};

const SMS_STATUS_LABELS: Record<PayrollStatementSmsDeliveryStatus, string> = {
  pending: "Text pending",
  enqueued: "Texting",
  succeeded: "Texted",
  failed: "Text failed",
  skipped: "Not texted",
};

export const getSentPackChangeLabel = (
  sentPack?: PayrollStatementSentPack | null,
) => {
  if (sentPack?.changed !== true) return null;

  return "Needs renotify";
};

export const getNotificationReasonLabel = (reason?: string | null) =>
  reason ? (REASON_LABELS[reason] ?? reason.replaceAll("_", " ")) : null;

export const getDeliveryStatusLabel = (
  status?: PayrollStatementNotificationDeliveryStatus | null,
) => (status ? STATUS_LABELS[status] : "Not sent");

export const getSmsDeliveryStatusLabel = (sms: PayrollStatementSmsDelivery) =>
  isSmsDeliveryWithCarrier(sms)
    ? "Sent to carrier"
    : SMS_STATUS_LABELS[sms.status];

export const getSmsDeliveryReason = (sms: PayrollStatementSmsDelivery) => {
  if (sms.status === "skipped")
    return getNotificationReasonLabel(sms.skip_reason);
  if (sms.status === "failed") return sms.error;
  return null;
};

export const formatTextNumber = (phoneNumber: string) =>
  phoneNumber.startsWith("+1")
    ? (formatPhoneNumber(phoneNumber) ?? phoneNumber)
    : phoneNumber;

export const formatNotificationTimestamp = (timestamp?: string | null) =>
  timestamp ? dayjs(timestamp).format("MMM D, YYYY h:mm A") : null;

export const formatNotificationPeriod = ({
  startDate,
  endDate,
  fallback,
}: {
  startDate?: string | null;
  endDate?: string | null;
  fallback: string;
}) => (startDate && endDate ? formatDateRange(startDate, endDate) : fallback);
